import type { ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Clock, ClipboardList } from 'lucide-react';
import { useOrgPublicProfile } from '@/contexts/OrgPublicProfileContext';
import { PublicOrgSubpageHero } from '@/components/public/PublicOrgSubpageHero';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { useAuthStore } from '@/store/auth';
import { useEmbedHost } from '@/contexts/EmbedHostContext';

type CourtsHubTab = 'book' | 'bookings';

const COURTS_HUB_TABS: {
  id: CourtsHubTab;
  label: string;
  mobileLabel: string;
  icon: ReactNode;
}[] = [
  { id: 'book', label: 'Book', mobileLabel: 'Book', icon: <Clock aria-hidden /> },
  {
    id: 'bookings',
    label: 'My bookings',
    mobileLabel: 'Bookings',
    icon: <ClipboardList aria-hidden />,
  },
];

export function OrgCourtsHubLayout() {
  const { profile } = useOrgPublicProfile();
  const { p } = useOrgPaths();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { isEmbedMode } = useEmbedHost();
  const consumerNav = useUnifiedConsumerNav();

  const bookPath = p('/court-hire');
  const bookingsPath = p('/court-hire/bookings');
  const showBookingsTab = Boolean(
    user && consumerNav.hasClubMemberAccess && consumerNav.hasCourtHire
  );
  const isBookingsTab = Boolean(bookingsPath && location.pathname.startsWith(bookingsPath));
  const activeTab: CourtsHubTab = isBookingsTab ? 'bookings' : 'book';

  const clubTitle = profile?.organization.name ?? 'Court hire';
  const bannerUrl = profile?.organization.bannerUrl;

  const handleTabChange = (tab: CourtsHubTab) => {
    if (tab === 'book' && bookPath) {
      navigate(bookPath);
      return;
    }
    if (tab === 'bookings' && bookingsPath) {
      navigate(bookingsPath);
    }
  };

  if (isEmbedMode) {
    return <Outlet />;
  }

  return (
    <>
      <PublicOrgSubpageHero
        bannerUrl={bannerUrl}
        title="Court hire"
        headingId="court-hire-hero-heading"
      >
        <p className="mt-3 max-w-2xl text-sm text-white/85 sm:text-base">
          {isBookingsTab
            ? `View your court and equipment reservations at ${clubTitle}.`
            : `Reserve a court at ${clubTitle} online — no account required.`}
        </p>
      </PublicOrgSubpageHero>

      <div className="bg-muted/30">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-10">
          {showBookingsTab && bookPath && bookingsPath ? (
            <DashboardTabNav<CourtsHubTab>
              role="tablist"
              tabs={COURTS_HUB_TABS}
              activeTab={activeTab}
              onChange={handleTabChange}
              ariaLabel="Court hire sections"
            />
          ) : null}

          <Outlet />
        </div>
      </div>
    </>
  );
}
